import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    TextField,
    InputAdornment,
    Avatar,
    Chip,
    Skeleton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from '@mui/material/styles';
import { getAllInvoices } from '../../../api_calls/api_calls_invoices';
import ConfirmPlanChangeModal from './ConfirmPlanChangeModal';

const PROVIDER_LOGO_PATH = '/images/providers/';

const ChangePlanModal = ({ open, onClose, houseId, onPlanSelect, currentPlanProviderName, currentPlanName }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [filteredPlans, setFilteredPlans] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [error, setError] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    useEffect(() => {
        if (open && houseId) {
            setLoading(true);
            setError(null);
            setSearchQuery('');

            getAllInvoices()
                .then((plans) => {
                    setPlans(plans || []);
                    setFilteredPlans(plans || []);
                    setLoading(false);
                })
                .catch((error) => {
                    setError(error.message || 'Failed to load plans');
                    setLoading(false);
                });
        } else if (!open) {
            setPlans([]);
            setFilteredPlans([]);
            setSearchQuery('');
            setError(null);
        }
    }, [open, houseId]);

    useEffect(() => {
        let filtered = plans;

        // Filter by search query if present
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = plans.filter(plan => {
                const displayName = (plan.display_name || '').toLowerCase();
                const providerName = (plan.provider_name || '').toLowerCase();
                const description = (plan.description || '').toLowerCase();
                return displayName.includes(query) || providerName.includes(query) || description.includes(query);
            });
        }

        // Sort: current plan first
        if (currentPlanProviderName && currentPlanName) {
            const currentPlanIndex = filtered.findIndex(
                plan => plan.provider_name === currentPlanProviderName && plan.name === currentPlanName
            );
            if (currentPlanIndex > 0) {
                const currentPlan = filtered[currentPlanIndex];
                filtered = [currentPlan, ...filtered.filter((_, i) => i !== currentPlanIndex)];
            }
        }

        setFilteredPlans(filtered);
    }, [searchQuery, plans, currentPlanProviderName, currentPlanName]);

    const getColorCode = (color) => {
        return color === 'green' ? '#4CAF50' : '#2196F3';
    };

    const getBgColorCode = (color) => {
        return color === 'green' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)';
    };

    const handlePlanClick = (plan) => {
        setSelectedPlan(plan);
        setConfirmModalOpen(true);
    };

    const handleConfirm = () => {
        if (onPlanSelect && selectedPlan) {
            onPlanSelect(selectedPlan);
        }
        setConfirmModalOpen(false);
        setSelectedPlan(null);
        onClose();
    };

    const handleCancel = () => {
        setConfirmModalOpen(false);
        setSelectedPlan(null);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '25px',
                    maxHeight: '90vh',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            width: 40,
                            height: 40
                        }}
                    >
                        <SettingsIcon sx={{ color: 'white' }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Edit Electricity Plan
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Select a new plan
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box>
                        <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={80} sx={{ mb: 1, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={80} sx={{ mb: 1, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={80} sx={{ mb: 1, borderRadius: 2 }} />
                    </Box>
                ) : error ? (
                    <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
                ) : (
                    <Box>
                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            placeholder="Search Plans..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Plans List */}
                        <Box sx={{ maxHeight: '60vh', overflowY: 'auto', p: 1 }}>
                            {filteredPlans.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                    {searchQuery ? 'No plans found matching your search.' : 'No plans available.'}
                                </Typography>
                            ) : (
                                filteredPlans.map((plan, index) => {
                                    const isCurrentPlan = plan.provider_name === currentPlanProviderName && plan.name === currentPlanName;
                                    return (
                                        <Box
                                            key={index}
                                            onClick={() => handlePlanClick(plan)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                padding: '16px',
                                                marginBottom: '12px',
                                                backgroundColor: isCurrentPlan ? theme.palette.primary.light + '20' : 'white',
                                                borderRadius: '12px',
                                                border: `1px solid ${isCurrentPlan ? theme.palette.primary.main : theme.palette.grey[200]}`,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.grey[50],
                                                    borderColor: theme.palette.primary.main,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 4px 8px ${theme.palette.grey[300]}`,
                                                },
                                            }}
                                        >
                                            {/* Provider Logo */}
                                            <Avatar
                                                src={PROVIDER_LOGO_PATH + plan.provider_name + '.png'}
                                                alt={plan.provider_name}
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    bgcolor: getBgColorCode(plan.color),
                                                    border: `2px solid ${getColorCode(plan.color)}`,
                                                }}
                                            />

                                            {/* Plan Info */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                                                    {plan.display_name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Chip
                                                        label={(plan.provider_name || '').replace(/_/g, ' ')}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getBgColorCode(plan.color),
                                                            color: getColorCode(plan.color),
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            height: '24px',
                                                        }}
                                                    />
                                                </Box>
                                                {plan.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {plan.description}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Arrow Icon */}
                                            <ArrowForwardIosIcon sx={{ color: theme.palette.grey[600], fontSize: 16 }} />
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <ConfirmPlanChangeModal
                open={confirmModalOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                selectedPlan={selectedPlan}
            />
        </Dialog>
    );
};

export default ChangePlanModal;

